<script setup lang="ts">
import { computed, onMounted, ref, useTemplateRef, watch } from 'vue';

import type { IUpdateInformation } from '@/Interface';
import type { ICredentialsDecryptedResponse, ICredentialsResponse } from '../../credentials.types';

import type {
	CredentialInformation,
	ICredentialDataDecryptedObject,
	ICredentialsDecrypted,
	INode,
	INodeParameters,
	INodeProperties,
	ITelemetryTrackProperties,
} from 'n8n-workflow';
import { CREDENTIAL_EMPTY_VALUE, deepCopy, NodeHelpers } from 'n8n-workflow';
import CredentialIcon from '../CredentialIcon.vue';

import CredentialConfig from './CredentialConfig.vue';
import CredentialInfo from './CredentialInfo.vue';
import CredentialSharing from './CredentialSharing.ee.vue';
import Modal from '@/app/components/Modal.vue';
import SaveButton from '@/app/components/SaveButton.vue';
import { useMessage } from '@/app/composables/useMessage';
import { useNodeHelpers } from '@/app/composables/useNodeHelpers';
import { useToast } from '@/app/composables/useToast';
import { CREDENTIAL_EDIT_MODAL_KEY } from '../../credentials.constants';
import { EnterpriseEditionFeature, MODAL_CONFIRM } from '@/app/constants';
import { useCredentialsStore } from '../../credentials.store';
import { injectNDVStore } from '@/features/ndv/shared/ndv.store';
import { useNodeTypesStore } from '@/app/stores/nodeTypes.store';
import { useSettingsStore } from '@/app/stores/settings.store';
import { useUIStore } from '@/app/stores/ui.store';
import { injectWorkflowDocumentStore } from '@/app/stores/workflowDocument.store';
import type { Project, ProjectSharingData } from '@/features/collaboration/projects/projects.types';
import { getResourcePermissions } from '@n8n/permissions';
import { assert } from '@n8n/utils/assert';
import { createEventBus } from '@n8n/utils/event-bus';

import { useExternalHooks } from '@/app/composables/useExternalHooks';
import { useTelemetry } from '@/app/composables/useTelemetry';
import { useProjectsStore } from '@/features/collaboration/projects/projects.store';
import { useExternalSecretsStore } from '@/features/integrations/externalSecrets.ee/externalSecrets.ee.store';
import { useRootStore } from '@n8n/stores/useRootStore';
import { sendUserEvent, type DynamicNotification } from '@n8n/rest-api-client/api/cloudPlans';
import { isExpression, isTestableExpression } from '@/app/utils/expressions';
import {
	getAppNameFromCredType,
	getNodeAuthOptions,
	getNodeCredentialForSelectedAuthType,
	updateNodeAuthType,
} from '@/app/utils/nodeTypesUtils';
import { isCredentialModalState, isValidCredentialResponse } from '@/app/utils/typeGuards';
import { useI18n, type BaseTextKey } from '@n8n/i18n';
import { useElementSize } from '@vueuse/core';
import { useRouter } from 'vue-router';

import {
	N8nIcon,
	N8nIconButton,
	N8nInlineTextEdit,
	N8nMenuItem,
	N8nTag,
	N8nText,
	type IMenuItem,
} from '@n8n/design-system';
import { setParameterValue } from '@/app/utils/parameterUtils';
import get from 'lodash/get';
import { useDynamicCredentials } from '@/features/resolvers/composables/useDynamicCredentials';
import { useQuickConnect } from '../../quickConnect/composables/useQuickConnect';
import type { CredentialModeOption } from './CredentialModeSelector.vue';

const MANAGED_CREDENTIAL_HIDDEN_PROPERTIES = new Set([
	'scope',
	'customScopes',
	'enabledScopes',
	'customScopesNotice',
]);

type Props = {
	modalName: string;
	activeId?: string;
	mode?: 'new' | 'edit';
};

const props = withDefaults(defineProps<Props>(), { mode: 'new', activeId: undefined });

const credentialsStore = useCredentialsStore();
const ndvStore = injectNDVStore();
const settingsStore = useSettingsStore();
const uiStore = useUIStore();
const nodeTypesStore = useNodeTypesStore();
const projectsStore = useProjectsStore();
const externalSecretsStore = useExternalSecretsStore();

const nodeHelpers = useNodeHelpers();
const externalHooks = useExternalHooks();
const toast = useToast();
const message = useMessage();
const i18n = useI18n();

const I18N_PREFIX = 'credentialEdit.credentialEdit.confirmMessage';

async function confirmModal(
	key: string,
	interpolate?: Record<string, string>,
	extra?: { cancelButtonText?: string },
): Promise<string | boolean> {
	const t = (suffix: string) =>
		i18n.baseText(
			`${I18N_PREFIX}.${key}.${suffix}` as BaseTextKey,
			interpolate ? { interpolate } : {},
		);

	const cancelButton =
		extra?.cancelButtonText !== undefined ? { cancelButtonText: extra.cancelButtonText } : {};

	return await message.confirm(t('message'), t('headline'), {
		confirmButtonText: i18n.baseText(`${I18N_PREFIX}.${key}.confirmButtonText` as BaseTextKey),
		...cancelButton,
	});
}
const telemetry = useTelemetry();
const router = useRouter();
const rootStore = useRootStore();
const { isEnabled: isDynamicCredentialsEnabled } = useDynamicCredentials();
const { getQuickConnectOption, connect: quickConnect } = useQuickConnect();
const isQuickConnectMode = ref(false);
const activeTab = ref('connection');
const authError = ref('');
const credentialId = ref('');
const credentialName = ref('');
const selectedCredential = ref('');
const credentialData = ref<ICredentialDataDecryptedObject>({});
const currentCredential = ref<ICredentialsResponse | ICredentialsDecryptedResponse | null>(null);
const modalBus = ref(createEventBus());
const isDeleting = ref(false);
const isSaving = ref(false);
const isTesting = ref(false);
const hasUnsavedChanges = ref(false);
const isSaved = ref(false);
const loading = ref(false);
const showValidationWarning = ref(false);
const testedSuccessfully = ref(false);
const isRetesting = ref(false);
const hasUserSpecifiedName = ref(false);
const isSharedWithChanged = ref(false);
const requiredCredentials = ref(false); // Are credentials required or optional for the node
const contentRef = ref<HTMLDivElement>();
const isSharedGlobally = ref(false);
const isResolvable = ref(false);
const connectedByMe = ref(false);
const useCustomOAuth = ref(false);
const pendingAuthType = ref<string | null>(null);
const credentialDataCache = ref<Record<string, ICredentialDataDecryptedObject>>({});

const workflowDocumentStore = injectWorkflowDocumentStore();

const contextNode = computed<INode | null>(() => {
	if (ndvStore.value.activeNode) return ndvStore.value.activeNode;
	const modalState = uiStore.modalsById[CREDENTIAL_EDIT_MODAL_KEY];
	if (isCredentialModalState(modalState) && modalState.contextNode) {
		return modalState.contextNode;
	}
	const fallbackName = isCredentialModalState(modalState) ? modalState.nodeName : undefined;
	return fallbackName ? (workflowDocumentStore.value?.getNodeByName(fallbackName) ?? null) : null;
});

const hideAskAssistant = computed<boolean>(() => {
	const modalState = uiStore.modalsById[CREDENTIAL_EDIT_MODAL_KEY];
	return isCredentialModalState(modalState) && modalState.hideAskAssistant === true;
});

const activeNodeType = computed(() => {
	const activeNode = contextNode.value;

	if (activeNode) {
		return nodeTypesStore.getNodeType(activeNode.type, activeNode.typeVersion);
	}
	return null;
});

const selectedCredentialType = computed(() => {
	if (props.mode !== 'new') {
		return null;
	}

	// If there is already selected type, use it
	if (selectedCredential.value !== '') {
		return credentialsStore.getCredentialTypeByName(selectedCredential.value) ?? null;
	} else if (requiredCredentials.value) {
		// Use the recommended auth option (managed OAuth sorts first) or the first available
		const nodeAuthOptions = getNodeAuthOptions(activeNodeType.value);
		if (nodeAuthOptions.length > 0 && activeNodeType.value?.credentials) {
			return getNodeCredentialForSelectedAuthType(activeNodeType.value, nodeAuthOptions[0].value);
		}
		// No auth options — fall back to the explicitly requested type or the first credential
		if (props.activeId) {
			const nodeCredential = activeNodeType.value?.credentials?.find(
				(c) => c.name === props.activeId,
			);
			if (nodeCredential) {
				return nodeCredential;
			}
		}
		return activeNodeType.value?.credentials?.[0] ?? null;
	}

	return null;
});

const credentialType = computed(() => {
	if (!credentialTypeName.value) {
		return null;
	}

	const type = credentialsStore.getCredentialTypeByName(credentialTypeName.value);

	if (!type) {
		return null;
	}

	return {
		...type,
		properties: getCredentialProperties(credentialTypeName.value),
	};
});

const credentialTypeName = computed(() => {
	if (props.mode === 'edit') {
		if (selectedCredential.value) return selectedCredential.value;
		if (currentCredential.value) {
			return currentCredential.value.type;
		}

		return null;
	}
	if (selectedCredentialType.value) {
		return selectedCredentialType.value.name;
	}
	return `${props.activeId}`;
});

const isEditingManagedCredential = computed(() => {
	if (!props.activeId) return false;
	return credentialsStore.getCredentialById(props.activeId)?.isManaged ?? false;
});

const isCredentialTestable = computed(() => {
	if (isOAuthType.value || !requiredPropertiesFilled.value) {
		return false;
	}

	const hasUntestableExpressions = credentialProperties.value.some((prop) => {
		const value = credentialData.value[prop.name];
		return typeof value === 'string' && isExpression(value) && !isTestableExpression(value);
	});
	if (hasUntestableExpressions) {
		return false;
	}

	const nodesThatCanTest = nodesWithAccess.value.filter((node) => {
		if (node.credentials) {
			// Returns a list of nodes that can test this credentials
			const eligibleTesters = node.credentials.filter((credential) => {
				return credential.name === credentialTypeName.value && credential.testedBy;
			});
			// If we have any node that can test, return true.
			return !!eligibleTesters.length;
		}
		return false;
	});

	return !!nodesThatCanTest.length || (!!credentialType.value && !!credentialType.value.test);
});

const nodesWithAccess = computed(() => {
	if (credentialTypeName.value) {
		return credentialsStore.getNodesWithAccess(credentialTypeName.value);
	}

	return [];
});

const parentTypes = computed(() => {
	if (credentialTypeName.value) {
		return getParentTypes(credentialTypeName.value);
	}

	return [];
});

const isOAuthType = computed(() => {
	return (
		!!credentialTypeName.value &&
		(((credentialTypeName.value === 'oAuth2Api' || parentTypes.value.includes('oAuth2Api')) &&
			(credentialData.value.grantType === 'authorizationCode' ||
				credentialData.value.grantType === 'pkce')) ||
			credentialTypeName.value === 'oAuth1Api' ||
			parentTypes.value.includes('oAuth1Api'))
	);
});

const managedOAuthAvailable = computed(() => {
	return (
		activeNodeType.value?.credentials?.some((type) => hasManagedOAuthCredentials(type.name)) ??
		false
	);
});

const isManagedOAuthMode = computed(
	() => isOAuthType.value && managedOAuthAvailable.value && !useCustomOAuth.value,
);

const isOAuthConnected = computed(() => {
	if (!isOAuthType.value) return false;
	if (isResolvable.value) return connectedByMe.value;
	return !!credentialData.value.oauthTokenData;
});
const credentialProperties = computed(() => {
	const type = credentialType.value;
	if (!type) {
		return [];
	}

	const properties = type.properties.filter((propertyData: INodeProperties) => {
		if (!displayCredentialParameter(propertyData)) {
			return false;
		}
		return useCustomOAuth.value || !type.__overwrittenProperties?.includes(propertyData.name);
	});

	/**
	 * If after all credentials overrides are applied only "notice"
	 * properties are left, do not return them. This will avoid
	 * showing notices that refer to a property that was overridden.
	 */
	if (properties.every((p) => p.type === 'notice')) {
		return [];
	}

	return properties;
});

const requiredPropertiesFilled = computed(() => {
	for (const property of credentialProperties.value) {
		if (property.required !== true) {
			continue;
		}

		const credentialProperty = credentialData.value[property.name];

		if (property.type === 'string' && !credentialProperty) {
			return false;
		}

		if (property.type === 'number') {
			const containsExpression =
				typeof credentialProperty === 'string' && credentialProperty.startsWith('=');

			if (typeof credentialProperty !== 'number' && !containsExpression) {
				return false;
			}
		}

		if (property.type === 'json' && credentialProperty) {
			const jsonValue = String(credentialProperty);
			const containsExpression = isExpression(jsonValue);
			// Sentinels represent a previously-saved value, and expressions are always valid
			if (!containsExpression && jsonValue !== CREDENTIAL_EMPTY_VALUE) {
				try {
					JSON.parse(jsonValue);
				} catch {
					return false;
				}
			}
		}
	}
	return true;
});

const credentialPermissions = computed(() => {
	return getResourcePermissions(
		(currentCredential.value as ICredentialsResponse)?.scopes ?? homeProject.value?.scopes,
	).credential;
});

const sidebarItems = computed(() => {
	const menuItems: IMenuItem[] = [
		{
			id: 'connection',
			label: i18n.baseText('credentialEdit.credentialEdit.connection'),
			position: 'top',
		},
		{
			id: 'sharing',
			label: i18n.baseText('credentialEdit.credentialEdit.sharing'),
			position: 'top',
		},
		{
			id: 'details',
			label: i18n.baseText('credentialEdit.credentialEdit.details'),
			position: 'top',
		},
	];

	return menuItems;
});

const defaultCredentialTypeName = computed(() => {
	let defaultName = credentialTypeName.value;
	if (!defaultName || defaultName === 'null') {
		if (activeNodeType.value?.credentials && activeNodeType.value.credentials.length > 0) {
			defaultName = activeNodeType.value.credentials[0].name;
		}
	}
	return defaultName ?? '';
});

const showSaveButton = computed(() => {
	if (isQuickConnectMode.value) return false;
	const hasPermission = credentialPermissions.value.create ?? credentialPermissions.value.update;
	if (!hasPermission) return false;
	if (isOAuthType.value && !isOAuthConnected.value) return false;
	return true;
});

const showHeaderSaveButton = computed(
	() =>
		showSaveButton.value &&
		!!credentialType.value &&
		(activeTab.value === 'connection' || activeTab.value === 'sharing'),
);

const showSharingContent = computed(() => activeTab.value === 'sharing' && !!credentialType.value);

// Whether the credential is already shared (with other projects or globally) as
// persisted. A shared credential can't be turned into a dynamic credential.
const isCurrentlyShared = computed(() => {
	const cred = currentCredential.value;
	if (!cred) return false;
	const sharedWithProjects = 'sharedWithProjects' in cred ? (cred.sharedWithProjects ?? []) : [];
	const isGlobal = 'isGlobal' in cred ? Boolean(cred.isGlobal) : false;
	return isGlobal || sharedWithProjects.length > 0;
});

const homeProject = computed(() => {
	const modalState = uiStore.modalsById[CREDENTIAL_EDIT_MODAL_KEY];
	const overrideProjectId = isCredentialModalState(modalState) ? modalState.projectId : undefined;
	if (overrideProjectId) {
		const override = projectsStore.myProjects.find((p) => p.id === overrideProjectId);
		if (override) return override;
	}
	const { currentProject, personalProject } = projectsStore;
	return currentProject ?? personalProject;
});

const isNewCredential = computed(() => props.mode === 'new' && !credentialId.value);

function setCredentialPropertyDefaults() {
	if (credentialType.value) {
		for (const property of credentialType.value.properties) {
			if (
				!credentialData.value.hasOwnProperty(property.name) &&
				!credentialType.value.__overwrittenProperties?.includes(property.name)
			) {
				credentialData.value = {
					...credentialData.value,
					[property.name]: property.default as CredentialInformation,
				};
			}
		}
	}
}

// For new credentials of skip-list types, default to custom mode (managed creation is disabled).
// Using { immediate: true } handles both initial load and subsequent type switches.
watch(
	credentialType,
	(newType) => {
		if (props.mode === 'new' && newType?.__skipManagedCreation) {
			useCustomOAuth.value = true;
		}
	},
	{ immediate: true },
);

onMounted(async () => {
	const modalState = uiStore.modalsById[CREDENTIAL_EDIT_MODAL_KEY];
	requiredCredentials.value =
		isCredentialModalState(modalState) && modalState.showAuthSelector === true;

	const forceManual = isCredentialModalState(modalState) && modalState.forceManualMode === true;

	const overrideProjectId = isCredentialModalState(modalState) ? modalState.projectId : undefined;
	const projectId =
		overrideProjectId ?? projectsStore.currentProjectId ?? projectsStore.personalProject?.id;
	if (projectId) {
		try {
			await externalSecretsStore.fetchSecretsForProject(projectId);
		} catch {
			// Secrets fetch failure should not block the credential modal
		}
	}

	if (props.mode === 'new' && credentialTypeName.value) {
		const modalSuggestedName = isCredentialModalState(modalState)
			? modalState.suggestedName
			: undefined;
		credentialName.value = modalSuggestedName
			? modalSuggestedName
			: await credentialsStore.getNewCredentialName({
					credentialTypeName: defaultCredentialTypeName.value,
				});

		credentialData.value = {
			...credentialData.value,
			...(homeProject.value ? { homeProject: homeProject.value } : {}),
		};
	} else {
		await loadCurrentCredential();
	}

	setCredentialPropertyDefaults();

	// Detect if existing credential uses custom OAuth (user-provided clientId/clientSecret).
	// Use __overwrittenProperties directly instead of managedOAuthAvailable so that skip-list
	// types (where managedOAuthAvailable is false) still auto-detect custom credentials.
	if (
		credentialType.value?.__overwrittenProperties?.includes('clientId') &&
		credentialData.value.clientId &&
		credentialData.value.clientSecret
	) {
		useCustomOAuth.value = true;
	}

	// Default to quick connect mode for new credentials when available and not forced to manual
	if (
		props.mode === 'new' &&
		!forceManual &&
		credentialTypeName.value &&
		ndvStore.value.activeNode
	) {
		const qcOption = getQuickConnectOption(
			credentialTypeName.value,
			ndvStore.value.activeNode.type,
		);
		if (qcOption) {
			isQuickConnectMode.value = true;
		}
	}

	await externalHooks.run('credentialsEdit.credentialModalOpened', {
		credentialType: credentialTypeName.value,
		isEditingCredential: props.mode === 'edit',
		activeNode: ndvStore.value.activeNode,
	});

	setTimeout(async () => {
		if (credentialId.value) {
			if (!requiredPropertiesFilled.value && credentialPermissions.value.update) {
				// sharees can't see properties, so this check would always fail for them
				// if the credential contains required fields.
				showValidationWarning.value = true;
			} else {
				await retestCredential();
			}
		}
	}, 0);

	loading.value = false;
});

async function beforeClose() {
	let keepEditing = false;

	if (hasUnsavedChanges.value && !isNewCredential.value) {
		const displayName = credentialType.value ? credentialType.value.displayName : '';
		const confirmAction = await confirmModal(
			'beforeClose1',
			{ credentialDisplayName: displayName },
			{ cancelButtonText: i18n.baseText(`${I18N_PREFIX}.beforeClose1.cancelButtonText`) },
		);
		keepEditing = confirmAction === MODAL_CONFIRM;
	} else if (
		credentialPermissions.value.update &&
		isOAuthType.value &&
		!isOAuthConnected.value &&
		// Private credentials are only the reusable "blueprint" — connecting is a
		// per-user step done later, so we don't prompt to connect before closing.
		!isResolvable.value
	) {
		const confirmAction = await confirmModal('beforeClose2', undefined, {
			cancelButtonText: i18n.baseText(`${I18N_PREFIX}.beforeClose2.cancelButtonText`),
		});
		keepEditing = confirmAction === MODAL_CONFIRM;
	}

	if (!keepEditing) {
		pendingAuthType.value = null;
		uiStore.activeCredentialType = null;
		return true;
	} else if (!requiredPropertiesFilled.value) {
		showValidationWarning.value = true;
		scrollToTop();
	} else if (isOAuthType.value) {
		scrollToBottom();
	}

	return false;
}

function displayCredentialParameter(parameter: INodeProperties): boolean {
	if (parameter.type === 'hidden') {
		return false;
	}

	if (
		MANAGED_CREDENTIAL_HIDDEN_PROPERTIES.has(parameter.name) &&
		(isEditingManagedCredential.value || isManagedOAuthMode.value)
	) {
		return false;
	}

	if (parameter.displayOptions?.hideOnCloud && settingsStore.isCloudDeployment) {
		return false;
	}

	if (parameter.displayOptions === undefined) {
		// If it is not defined no need to do a proper check
		return true;
	}

	return nodeHelpers.displayParameter(credentialData.value as INodeParameters, parameter, '', null);
}

function getCredentialProperties(name: string): INodeProperties[] {
	const credentialTypeData = credentialsStore.getCredentialTypeByName(name);

	if (!credentialTypeData) {
		return [];
	}

	if (credentialTypeData.extends === undefined) {
		return credentialTypeData.properties;
	}

	const combineProperties = [] as INodeProperties[];
	for (const credentialsTypeName of credentialTypeData.extends) {
		const mergeCredentialProperties = getCredentialProperties(credentialsTypeName);
		NodeHelpers.mergeNodeProperties(combineProperties, mergeCredentialProperties);
	}

	// The properties defined on the parent credentials take precedence
	NodeHelpers.mergeNodeProperties(combineProperties, credentialTypeData.properties);

	return combineProperties;
}

/**
 *
 * We might get credential with empty parameters from source-control
 * which breaks our types and Fe checks
 */
function removePropertiesWithEmptyStrings<T extends { [key: string]: unknown }>(data: T): T {
	const copy = structuredClone(data);
	Object.entries(copy).forEach(([key, value]) => {
		if (value === '') delete copy[key];
	});
	return copy;
}

async function loadCurrentCredential(id = props.activeId ?? '') {
	credentialId.value = id;

	try {
		const currentCredentials = await credentialsStore.getCredentialData({
			id: credentialId.value,
		});

		if (!currentCredentials) {
			throw new Error(
				i18n.baseText('credentialEdit.credentialEdit.couldNotFindCredentialWithId') +
					':' +
					credentialId.value,
			);
		}

		currentCredential.value = currentCredentials;

		credentialData.value = removePropertiesWithEmptyStrings(
			(currentCredentials.data as ICredentialDataDecryptedObject) || {},
		);

		if (currentCredentials.sharedWithProjects) {
			credentialData.value = {
				...credentialData.value,
				sharedWithProjects: currentCredentials.sharedWithProjects,
			};
		}
		if (currentCredentials.homeProject) {
			credentialData.value = {
				...credentialData.value,
				homeProject: currentCredentials.homeProject,
			};
		}

		credentialName.value = currentCredentials.name;
		isSharedGlobally.value =
			'isGlobal' in currentCredentials && typeof currentCredentials.isGlobal === 'boolean'
				? currentCredentials.isGlobal
				: false;
		isResolvable.value =
			'isResolvable' in currentCredentials && typeof currentCredentials.isResolvable === 'boolean'
				? currentCredentials.isResolvable
				: false;
		connectedByMe.value =
			'connectedByMe' in currentCredentials && typeof currentCredentials.connectedByMe === 'boolean'
				? currentCredentials.connectedByMe
				: false;
	} catch (error) {
		toast.showError(
			error,
			i18n.baseText('credentialEdit.credentialEdit.showError.loadCredential.title'),
		);
		closeDialog();

		return;
	}
}

function onTabSelect(tab: string) {
	activeTab.value = tab;
	const credType: string = credentialType.value ? credentialType.value.name : '';
	const activeNode: INode | null = ndvStore.value.activeNode;

	telemetry.track('User viewed credential tab', {
		credential_type: credType,
		node_type: activeNode ? activeNode.type : null,
		tab,
		workflow_id: workflowDocumentStore.value.workflowId,
		credential_id: credentialId.value,
		sharing_enabled: EnterpriseEditionFeature.Sharing,
	});
}

function onChangeSharedWith(sharedWithProjects: ProjectSharingData[]) {
	credentialData.value = {
		...credentialData.value,
		sharedWithProjects,
	};
	isSharedWithChanged.value = true;
	hasUnsavedChanges.value = true;
}

function onShareWithAllUsersUpdate(shareWithAllUsers: boolean) {
	isSharedGlobally.value = shareWithAllUsers;
	hasUnsavedChanges.value = true;
}

function getCurrentModeCacheKey(): string {
	const base = credentialTypeName.value ?? '';
	if (isOAuthType.value) {
		return `${base}:${useCustomOAuth.value ? 'custom' : 'managed'}`;
	}
	return base;
}

function cacheCurrentData(): void {
	const key = getCurrentModeCacheKey();
	if (!key) return;

	credentialDataCache.value[key] = { ...credentialData.value };
}

function restoreOrReset(): void {
	const cached = credentialDataCache.value[getCurrentModeCacheKey()];
	if (cached) {
		credentialData.value = { ...cached };
	} else {
		resetCredentialData();
	}
}

async function onResolvableChange(value: boolean) {
	const credName = credentialName.value;
	const isTogglingToPrivate = value && !isResolvable.value;
	const isTogglingToStatic = !value && isResolvable.value;

	if (isTogglingToPrivate && credentialData.value.oauthTokenData) {
		// Static → Private: warn only when there is a shared token to lose
		const confirmAction = await confirmModal('switchToPrivate', { credentialName: credName });

		if (confirmAction !== MODAL_CONFIRM) {
			return;
		}
	} else if (isTogglingToStatic) {
		// Private → Static: warn only when there are connected users to disconnect.
		// `connectedUserCount` reflects the server state at modal-open and isn't
		// refreshed when the current user connects within the same session, so fold
		// in `connectedByMe` to make sure the warning still appears in that case.
		const serverConnectedCount = currentCredential.value?.connectedUserCount ?? 0;
		const connectedUserCount = Math.max(serverConnectedCount, connectedByMe.value ? 1 : 0);
		if (connectedUserCount > 0) {
			const confirmAction = await confirmModal('switchToStatic', {
				count: String(connectedUserCount),
				credentialName: credName,
			});

			if (confirmAction !== MODAL_CONFIRM) {
				return;
			}
		}
	}

	isResolvable.value = value;
	// Switching sharing mode invalidates any carried-over connection state: a
	// freshly-private credential has no per-user connection for the current
	// user yet, so reset it to avoid rendering a stale "connected" state with a
	// Disconnect button that has nothing to disconnect.
	connectedByMe.value = false;
	hasUnsavedChanges.value = true;
}

function onDataChange({ name, value }: IUpdateInformation) {
	const currentValue = get(credentialData.value, name);
	if (currentValue === value) {
		return;
	}

	hasUnsavedChanges.value = true;

	if (isOAuthType.value && (name === 'clientId' || name === 'clientSecret')) {
		const { oauthTokenData, ...credData } = credentialData.value;
		credentialData.value = deepCopy(credData);
	}

	setParameterValue(credentialData.value, name, value);
}

function closeDialog() {
	modalBus.value.emit('close');
}

function getParentTypes(name: string): string[] {
	const type = credentialsStore.getCredentialTypeByName(name);

	if (type?.extends === undefined) {
		return [];
	}

	const types: string[] = [];
	for (const typeName of type.extends) {
		types.push(typeName);
		types.push.apply(types, getParentTypes(typeName));
	}

	return types;
}

function onNameEdit(text: string) {
	hasUnsavedChanges.value = true;
	hasUserSpecifiedName.value = true;
	credentialName.value = text;
}

function scrollToTop() {
	setTimeout(() => {
		if (contentRef.value) {
			contentRef.value.scrollTop = 0;
		}
	}, 0);
}

function scrollToBottom() {
	setTimeout(() => {
		if (contentRef.value) {
			contentRef.value.scrollTop = contentRef.value.scrollHeight;
		}
	}, 0);
}

async function retestCredential() {
	if (isEditingManagedCredential.value) {
		return;
	}

	if (!isCredentialTestable.value || !credentialTypeName.value) {
		authError.value = '';
		testedSuccessfully.value = false;

		return;
	}

	const { ownedBy, sharedWithProjects, ...otherCredData } = credentialData.value;
	const details: ICredentialsDecrypted = {
		id: credentialId.value,
		name: credentialName.value,
		type: credentialTypeName.value,
		data: otherCredData,
	};

	isRetesting.value = true;
	await testCredential(details);
	isRetesting.value = false;
}

async function testCredential(credentialDetails: ICredentialsDecrypted) {
	const result = await credentialsStore.testCredential(credentialDetails);
	if (result.status === 'Error') {
		authError.value = result.message;
		testedSuccessfully.value = false;
	} else {
		authError.value = '';
		testedSuccessfully.value = true;
	}

	scrollToTop();
}

function usesExternalSecrets(data: Record<string, unknown>): boolean {
	return Object.entries(data).some(
		([, value]) => typeof value !== 'object' && /=.*\{\{[^}]*\$secrets\.[^}]+}}.*/.test(`${value}`),
	);
}

function hasManagedOAuthCredentials(credType: string) {
	const type = credentialsStore.getCredentialTypeByName(credType);
	if (type?.__skipManagedCreation) return false;
	return (
		type?.__overwrittenProperties?.includes('clientId') &&
		type.__overwrittenProperties.includes('clientSecret')
	);
}

async function saveCredential(): Promise<ICredentialsResponse | null> {
	if (!requiredPropertiesFilled.value) {
		showValidationWarning.value = true;
		scrollToTop();
		return null;
	} else {
		showValidationWarning.value = false;
	}

	isSaving.value = true;

	// Save only the none default data
	assert(credentialType.value);
	const data = NodeHelpers.getNodeParameters(
		credentialType.value.properties,
		credentialData.value as INodeParameters,
		false,
		false,
		null,
		null,
	);

	assert(credentialTypeName.value);
	const credentialDetails: ICredentialsDecrypted = {
		id: credentialId.value,
		name: credentialName.value,
		type: credentialTypeName.value,
		data: data as unknown as ICredentialDataDecryptedObject,
		isGlobal: isSharedGlobally.value,
		isResolvable: isResolvable.value,
	};

	if (
		settingsStore.isEnterpriseFeatureEnabled[EnterpriseEditionFeature.Sharing] &&
		credentialData.value.sharedWithProjects
	) {
		credentialDetails.sharedWithProjects = credentialData.value
			.sharedWithProjects as ProjectSharingData[];
	}

	if (credentialData.value.homeProject) {
		credentialDetails.homeProject = credentialData.value.homeProject as ProjectSharingData;
	}

	const appliedAuthType = pendingAuthType.value;
	if (appliedAuthType && contextNode.value) {
		updateNodeAuthType(
			workflowDocumentStore.value.updateNodeProperties,
			contextNode.value,
			appliedAuthType,
		);
		pendingAuthType.value = null;
	}

	let credential: ICredentialsResponse | null = null;

	const isNewCredential = props.mode === 'new' && !credentialId.value;

	if (isNewCredential) {
		credential = await createCredential(credentialDetails, projectsStore.currentProject);
	} else {
		if (settingsStore.isEnterpriseFeatureEnabled[EnterpriseEditionFeature.Sharing]) {
			credentialDetails.sharedWithProjects = credentialData.value
				.sharedWithProjects as ProjectSharingData[];
		}

		credential = await updateCredential(credentialDetails);
	}

	isSaving.value = false;
	if (credential) {
		credentialId.value = credential.id;
		currentCredential.value = credential;

		// Re-fetch to display server-redacted JSON shape for credentials with leaf-redacted fields
		if (credentialProperties.value.some((p) => p.typeOptions?.redactJsonLeaves)) {
			await loadCurrentCredential(credential.id);
			setCredentialPropertyDefaults();
		}

		if (isCredentialTestable.value) {
			isTesting.value = true;
			// Add the full data including defaults for testing
			credentialDetails.data = credentialData.value;

			credentialDetails.id = credentialId.value;

			await testCredential(credentialDetails);
			isTesting.value = false;
		} else {
			authError.value = '';
			testedSuccessfully.value = false;
		}

		const trackProperties: ITelemetryTrackProperties = {
			credential_type: credentialDetails.type,
			workflow_id: workflowDocumentStore.value.workflowId,
			credential_id: credential.id,
			is_complete: !!requiredPropertiesFilled.value,
			is_new: isNewCredential,
			uses_external_secrets: usesExternalSecrets(credentialDetails.data ?? {}),
		};

		if (isOAuthType.value) {
			trackProperties.is_valid = !!isOAuthConnected.value;
		} else if (isCredentialTestable.value) {
			trackProperties.is_valid = !!testedSuccessfully.value;
		}

		if (ndvStore.value.activeNode) {
			trackProperties.node_type = ndvStore.value.activeNode.type;
		}

		if (authError.value && authError.value !== '') {
			trackProperties.authError = authError.value;
		}

		/**
		 * For non-OAuth credentials we track saving on clicking the `Save` button, but for
		 * OAuth credentials we track saving at the end of the flow (BroastcastChannel event)
		 * so that the `is_valid` property is correct.
		 */
		if (!isOAuthType.value) {
			telemetry.track('User saved credentials', trackProperties);
			void handleDynamicNotification(!!trackProperties.is_valid);
		}

		await externalHooks.run('credentialEdit.saveCredential', trackProperties);
	}

	return credential;
}

async function handleDynamicNotification(isValid: boolean) {
	if (!isValid || !settingsStore.isCloudDeployment) {
		return;
	}

	try {
		const response: DynamicNotification = await sendUserEvent(rootStore.restApiContext, {
			eventType: 'credential-saved',
			metadata: {
				credential_type: credentialTypeName.value,
			},
		});

		if (response.title && response.message) {
			setTimeout(async () => {
				try {
					await message.confirm(response.message, response.title, {
						confirmButtonText: i18n.baseText('generic.keepBuilding'),
						cancelButtonText: '',
						showCancelButton: false,
						closeOnClickModal: true,
						closeOnPressEscape: true,
					});
				} catch (error) {
					// Silently fail
				}
			}, 15000);
		}
	} catch (error) {
		// Silently fail
	}
}

const createToastMessagingForNewCredentials = (project?: Project | null) => {
	let toastTitle = i18n.baseText('credentials.create.personal.toast.title');
	let toastText = '';

	if (
		projectsStore.currentProject &&
		projectsStore.currentProject.id !== projectsStore.personalProject?.id
	) {
		toastTitle = i18n.baseText('credentials.create.project.toast.title', {
			interpolate: { projectName: project?.name ?? '' },
		});

		toastText = i18n.baseText('credentials.create.project.toast.text', {
			interpolate: { projectName: project?.name ?? '' },
		});
	}

	return {
		title: toastTitle,
		message: toastText,
	};
};

async function createCredential(
	credentialDetails: ICredentialsDecrypted,
	project?: Project | null,
): Promise<ICredentialsResponse | null> {
	let credential;

	try {
		credential = await credentialsStore.createNewCredential(
			credentialDetails,
			project?.id,
			router.currentRoute.value.query.uiContext?.toString(),
		);

		// eslint-disable-next-line @typescript-eslint/no-unused-vars
		const { uiContext, ...rest } = router.currentRoute.value.query;
		void router.replace({ query: rest });

		hasUnsavedChanges.value = false;

		const { title, message } = createToastMessagingForNewCredentials(project);

		toast.showMessage({
			title,
			message,
			type: 'success',
		});
	} catch (error) {
		toast.showError(
			error,
			i18n.baseText('credentialEdit.credentialEdit.showError.createCredential.title'),
		);

		return null;
	}

	await externalHooks.run('credential.saved', {
		credential_type: credentialDetails.type,
		credential_id: credential.id,
		is_new: true,
	});

	telemetry.track('User created credentials', {
		credential_type: credentialDetails.type,
		credential_id: credential.id,
		workflow_id: workflowDocumentStore.value.workflowId,
	});

	return credential;
}

async function updateCredential(
	credentialDetails: ICredentialsDecrypted,
): Promise<ICredentialsResponse | null> {
	let credential: ICredentialsResponse | null = null;
	try {
		if (credentialPermissions.value.update) {
			credential = await credentialsStore.updateCredential({
				id: credentialId.value,
				data: credentialDetails,
			});
		}
		if (
			credentialPermissions.value.share &&
			isSharedWithChanged.value &&
			credentialDetails.sharedWithProjects
		) {
			credential = await credentialsStore.setCredentialSharedWith({
				credentialId: credentialDetails.id,
				sharedWithProjects: credentialDetails.sharedWithProjects,
			});
			isSharedWithChanged.value = false;
		}
		hasUnsavedChanges.value = false;
		isSaved.value = true;

		if (credential) {
			await externalHooks.run('credential.saved', {
				credential_type: credentialDetails.type,
				credential_id: credential.id,
				is_new: false,
			});
		}
	} catch (error) {
		toast.showError(
			error,
			i18n.baseText('credentialEdit.credentialEdit.showError.updateCredential.title'),
		);

		return null;
	}

	// Now that the credentials changed check if any nodes use credentials
	// which have now a different name
	nodeHelpers.updateNodesCredentialsIssues();

	return credential;
}

async function deleteCredential() {
	if (!currentCredential.value) {
		return;
	}

	const savedCredentialName = currentCredential.value.name;

	const deleteConfirmed = await confirmModal('deleteCredential', { savedCredentialName });

	if (deleteConfirmed !== MODAL_CONFIRM) {
		return;
	}

	try {
		isDeleting.value = true;
		await credentialsStore.deleteCredential({ id: credentialId.value });
		hasUnsavedChanges.value = false;
		isSaved.value = true;
	} catch (error) {
		toast.showError(
			error,
			i18n.baseText('credentialEdit.credentialEdit.showError.deleteCredential.title'),
		);
		isDeleting.value = false;

		return;
	}

	isDeleting.value = false;
	// Now that the credentials were removed check if any nodes used them
	nodeHelpers.updateNodesCredentialsIssues();
	credentialData.value = {};

	toast.showMessage({
		title: i18n.baseText('credentialEdit.credentialEdit.showMessage.title'),
		type: 'success',
	});
	closeDialog();
}

async function oAuthCredentialAuthorize() {
	let url;

	credentialsStore.pendingOAuthRefresh = true;
	const credential = await saveCredential();
	if (!credential) {
		return;
	}

	const types = parentTypes.value;

	try {
		// We exclude sharedWithProjects because it's not needed for the authorization and it causes the request to be too large
		const { sharedWithProjects, ...sanitizedCredData } = credentialData.value;
		const credData = { id: credential.id, ...sanitizedCredData };

		if (credentialTypeName.value === 'oAuth2Api' || types.includes('oAuth2Api')) {
			if (isValidCredentialResponse(credData)) {
				url = await credentialsStore.oAuth2Authorize(credData);
			}
		} else if (credentialTypeName.value === 'oAuth1Api' || types.includes('oAuth1Api')) {
			if (isValidCredentialResponse(credData)) {
				url = await credentialsStore.oAuth1Authorize(credData);
			}
		}
	} catch (error) {
		toast.showError(
			error,
			i18n.baseText('credentialEdit.credentialEdit.showError.generateAuthorizationUrl.title'),
			{
				message: i18n.baseText(
					'credentialEdit.credentialEdit.showError.generateAuthorizationUrl.message',
				),
			},
		);

		return;
	}

	if (url === undefined || url === '') {
		toast.showError(
			new Error(i18n.baseText('credentialEdit.credentialEdit.showError.invalidOAuthUrl.message')),
			i18n.baseText('credentialEdit.credentialEdit.showError.invalidOAuthUrl.title'),
		);
		return;
	}

	// Prevent javascript:, data:, vbscript: and other non-http(s) protocols (XSS)
	const allowedOAuthUrlProtocols = ['http:', 'https:'];
	try {
		const parsedUrl = new URL(url);
		if (!allowedOAuthUrlProtocols.includes(parsedUrl.protocol)) {
			toast.showError(
				new Error(i18n.baseText('credentialEdit.credentialEdit.showError.invalidOAuthUrl.message')),
				i18n.baseText('credentialEdit.credentialEdit.showError.invalidOAuthUrl.title'),
			);
			return;
		}
	} catch {
		toast.showError(
			new Error(i18n.baseText('credentialEdit.credentialEdit.showError.invalidOAuthUrl.message')),
			i18n.baseText('credentialEdit.credentialEdit.showError.invalidOAuthUrl.title'),
		);
		return;
	}

	const params =
		'scrollbars=no,resizable=yes,status=no,titlebar=noe,location=no,toolbar=no,menubar=no,width=500,height=700';
	const oauthPopup = window.open(url, 'OAuth Authorization', params);

	credentialData.value = {
		...credentialData.value,
		oauthTokenData: null as unknown as CredentialInformation,
	};

	const oauthChannel = new BroadcastChannel('oauth-callback');
	const receiveMessage = (event: MessageEvent) => {
		const successfullyConnected = event.data === 'success';

		const trackProperties: ITelemetryTrackProperties = {
			credential_type: credentialTypeName.value,
			workflow_id: workflowDocumentStore.value.workflowId || null,
			credential_id: credentialId.value,
			is_complete: !!requiredPropertiesFilled.value,
			is_new: props.mode === 'new' && !credentialId.value,
			is_valid: successfullyConnected,
			uses_external_secrets: usesExternalSecrets(credentialData.value),
		};

		if (ndvStore.value.activeNode) {
			trackProperties.node_type = ndvStore.value.activeNode.type;
		}

		telemetry.track('User saved credentials', trackProperties);
		void handleDynamicNotification(successfullyConnected);

		if (successfullyConnected) {
			oauthChannel.removeEventListener('message', receiveMessage);

			// Set some kind of data that status changes.
			// As data does not get displayed directly it does not matter what data.
			credentialData.value = {
				...credentialData.value,
				oauthTokenData: {} as CredentialInformation,
			};

			connectedByMe.value = true;

			void credentialsStore.fetchAllCredentials().then(() => {
				nodeHelpers.updateNodesCredentialsIssues();
			});

			void credentialsStore.fetchAllCredentials().then(() => {
				nodeHelpers.updateNodesCredentialsIssues();
			});

			// Close the window
			if (oauthPopup) {
				oauthPopup.close();
			}
		}
	};
	oauthChannel.addEventListener('message', receiveMessage);
}

async function onDisconnectMyConnection(): Promise<void> {
	if (!credentialId.value) return;

	const confirmed = await message.confirm(
		i18n.baseText('credentialEdit.credentialEdit.confirmMessage.disconnectCredential.message', {
			interpolate: { savedCredentialName: credentialName.value },
		}),
		i18n.baseText('credentialEdit.credentialEdit.confirmMessage.disconnectCredential.headline'),
		{
			confirmButtonText: i18n.baseText(
				'credentialEdit.credentialEdit.confirmMessage.disconnectCredential.confirmButtonText',
			),
		},
	);

	if (confirmed !== MODAL_CONFIRM) return;

	try {
		await credentialsStore.disconnectMyConnection({ id: credentialId.value });
		connectedByMe.value = false;
		credentialData.value = {
			...credentialData.value,
			oauthTokenData: null as unknown as CredentialInformation,
		};
		toast.showMessage({
			title: i18n.baseText('credentialEdit.credentialEdit.showMessage.disconnected.title'),
			type: 'success',
		});
	} catch (error) {
		toast.showError(
			error,
			i18n.baseText('credentialEdit.credentialEdit.showError.disconnectCredential.title'),
		);
	}
}

async function onAuthTypeChanged(payload: CredentialModeOption): Promise<void> {
	if (payload.quickConnectEnabled) {
		isQuickConnectMode.value = true;
		return;
	}

	isQuickConnectMode.value = false;
	cacheCurrentData();
	authError.value = '';
	useCustomOAuth.value = payload.customOauth ?? false;

	if (!activeNodeType.value?.credentials) {
		return;
	}
	const credentialsForType = getNodeCredentialForSelectedAuthType(
		activeNodeType.value,
		payload.type,
	);
	if (credentialsForType) {
		selectedCredential.value = credentialsForType.name;
		uiStore.activeCredentialType = credentialsForType.name;

		restoreOrReset();

		pendingAuthType.value = payload.type;
		// Also update credential name but only if the default name is still used
		if (hasUnsavedChanges.value && !hasUserSpecifiedName.value) {
			const newDefaultName = await credentialsStore.getNewCredentialName({
				credentialTypeName: defaultCredentialTypeName.value,
			});
			credentialName.value = newDefaultName;
		}
	}
}

async function onQuickConnect(): Promise<void> {
	if (!credentialTypeName.value || !ndvStore.value.activeNode) return;

	const serviceName = getAppNameFromCredType(credentialType.value?.displayName ?? '');

	const credential = await quickConnect({
		credentialTypeName: credentialTypeName.value,
		nodeType: ndvStore.value.activeNode.type,
		source: 'credential_type',
		serviceName,
	});

	if (credential) {
		// Created credential does not include data, so we need to load it
		await loadCurrentCredential(credential.id);
		setCredentialPropertyDefaults();

		isQuickConnectMode.value = false;
		testedSuccessfully.value = true;
	}
}

function resetCredentialData(): void {
	if (!credentialType.value) {
		return;
	}
	for (const property of credentialType.value.properties) {
		if (!credentialType.value.__overwrittenProperties?.includes(property.name)) {
			credentialData.value = {
				...credentialData.value,
				[property.name]: property.default as CredentialInformation,
			};
		}
	}

	const resolvedProject = homeProject.value ?? {};
	const scopes = ('scopes' in resolvedProject ? resolvedProject.scopes : undefined) ?? [];

	credentialData.value = {
		...credentialData.value,
		scopes: scopes as unknown as CredentialInformation,
		homeProject: resolvedProject,
	};
}

const credNameRef = useTemplateRef('credNameRef');
const { width } = useElementSize(credNameRef);
</script>

<template>
	<Modal
		:name="modalName"
		:custom-class="$style.credentialModal"
		:event-bus="modalBus"
		:loading="loading"
		:before-close="beforeClose"
		width="70%"
		height="80%"
	>
		<template #header>
			<div :class="$style.header">
				<div :class="$style.credInfo">
					<div :class="$style.credIcon">
						<CredentialIcon :credential-type-name="defaultCredentialTypeName" />
					</div>
					<div ref="credNameRef" :class="$style.credName">
						<div :class="$style.credNameRow">
							<N8nInlineTextEdit
								v-if="credentialName"
								data-test-id="credential-name"
								:model-value="credentialName"
								:max-width="width - 10"
								:readonly="
									!(
										(credentialPermissions.create && props.mode === 'new') ||
										credentialPermissions.update
									) ||
									!credentialType ||
									isEditingManagedCredential
								"
								@update:model-value="onNameEdit"
							/>
							<N8nTag
								v-if="isResolvable"
								:text="i18n.baseText('credentialEdit.credentialEdit.dynamic')"
								:clickable="false"
								:class="$style.dynamicTag"
								data-test-id="credential-dynamic-tag"
							>
								<template #tag>
									<span :class="$style.dynamicTagContent">
										<N8nIcon icon="key-round" size="xsmall" />
										{{ i18n.baseText('credentialEdit.credentialEdit.dynamic') }}
									</span>
								</template>
							</N8nTag>
						</div>
						<N8nText v-if="credentialType" size="small" tag="p" color="text-light">{{
							credentialType.displayName
						}}</N8nText>
					</div>
				</div>
				<div :class="$style.credActions">
					<SaveButton
						v-if="showHeaderSaveButton"
						:class="$style.saveButton"
						:disabled="!hasUnsavedChanges && !isTesting && !!credentialId"
						:is-saving="isSaving || isTesting"
						:saved="!hasUnsavedChanges && !isTesting && !!credentialId"
						:saving-label="
							isTesting
								? i18n.baseText('credentialEdit.credentialEdit.testing')
								: i18n.baseText('credentialEdit.credentialEdit.saving')
						"
						data-test-id="credential-save-button"
						@click="saveCredential"
					/>
					<N8nIconButton
						variant="subtle"
						v-if="currentCredential && credentialPermissions.delete"
						:title="i18n.baseText('credentialEdit.credentialEdit.delete')"
						icon="trash-2"
						:disabled="isSaving"
						:loading="isDeleting"
						data-test-id="credential-delete-button"
						@click="deleteCredential"
					/>
				</div>
			</div>
		</template>
		<template #content>
			<div :class="$style.container" data-test-id="credential-edit-dialog">
				<div v-if="!isEditingManagedCredential" :class="$style.sidebar">
					<N8nMenuItem
						v-for="item in sidebarItems"
						:key="item.id"
						:item="item"
						:active="activeTab === item.id"
						@click="() => onTabSelect(item.id)"
					/>
				</div>
				<div
					v-if="activeTab === 'connection' && credentialType"
					ref="contentRef"
					:class="$style.mainContent"
				>
					<CredentialConfig
						:credential-type="credentialType"
						:credential-properties="credentialProperties"
						:credential-data="credentialData"
						:credential-id="credentialId"
						:is-managed="isEditingManagedCredential"
						:show-validation-warning="showValidationWarning"
						:auth-error="authError"
						:tested-successfully="testedSuccessfully"
						:is-o-auth-type="isOAuthType"
						:is-o-auth-connected="isOAuthConnected"
						:is-retesting="isRetesting"
						:parent-types="parentTypes"
						:required-properties-filled="requiredPropertiesFilled"
						:credential-permissions="credentialPermissions"
						:mode="mode"
						:selected-credential="selectedCredential"
						:is-dynamic-credentials-enabled="isDynamicCredentialsEnabled"
						:is-resolvable="isResolvable"
						:is-shared="isCurrentlyShared"
						:connected-by-me="connectedByMe"
						:is-new-credential="isNewCredential"
						:managed-oauth-available="managedOAuthAvailable"
						:use-custom-oauth="useCustomOAuth"
						:is-quick-connect-mode="isQuickConnectMode"
						:context-node="contextNode"
						:hide-ask-assistant="hideAskAssistant"
						@update="onDataChange"
						@oauth="oAuthCredentialAuthorize"
						@disconnect="onDisconnectMyConnection"
						@quick-connect="onQuickConnect"
						@retest="retestCredential"
						@scroll-to-top="scrollToTop"
						@auth-type-changed="onAuthTypeChanged"
						@claimed="closeDialog"
						@update:is-resolvable="onResolvableChange"
					/>
				</div>
				<div v-else-if="showSharingContent" :class="$style.mainContent">
					<CredentialSharing
						:credential="currentCredential"
						:credential-data="credentialData"
						:credential-id="credentialId"
						:credential-permissions="credentialPermissions"
						:is-shared-globally="isSharedGlobally"
						:is-resolvable="isResolvable"
						:modal-bus="modalBus"
						@update:model-value="onChangeSharedWith"
						@update:share-with-all-users="onShareWithAllUsersUpdate"
					/>
				</div>
				<div v-else-if="activeTab === 'details' && credentialType" :class="$style.mainContent">
					<CredentialInfo :current-credential="currentCredential" />
				</div>
			</div>
		</template>
	</Modal>
</template>

<style module lang="scss">
.credentialModal {
	--dialog--max-width: 1200px;
	--dialog--close--spacing--top: 36px;
	--dialog--max-height: 750px;

	:global(.el-dialog__header) {
		padding-bottom: 0;
		border-bottom: var(--border);
	}

	:global(.el-dialog__body) {
		padding-top: var(--spacing--lg);
		position: relative;
	}
}

.mainContent {
	flex: 1;
	overflow: auto;
	padding-bottom: var(--spacing--lg);
	padding-inline: var(--spacing--4xs);
}

.credName {
	display: flex;
	width: 100%;
	flex-direction: column;
	gap: var(--spacing--4xs);
}

.credNameRow {
	display: flex;
	align-items: center;
	gap: var(--spacing--2xs);
	min-height: var(--spacing--md);
}

.dynamicTagContent {
	display: inline-flex;
	align-items: center;
	gap: var(--spacing--5xs);
}

.sidebar {
	max-width: 170px;
	min-width: 170px;
	margin-right: var(--spacing--lg);
	flex-grow: 1;

	ul {
		padding: 0 !important;
	}
}

.header {
	display: flex;
}

.container {
	display: flex;
	height: 100%;
}

.credInfo {
	display: flex;
	align-items: center;
	flex-direction: row;
	flex-grow: 1;
	margin-bottom: var(--spacing--lg);
}

.credActions {
	display: flex;
	flex-direction: row;
	align-items: center;
	gap: var(--spacing--2xs);
	margin-right: var(--spacing--xl);
	margin-bottom: var(--spacing--lg);
	flex-shrink: 0;
}

.credIcon {
	display: flex;
	align-items: center;
	margin-right: var(--spacing--xs);
}

.saveButton {
	flex-shrink: 0;
}
</style>
